import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Settings2, 
  Trash2, 
  Edit2, 
  Search, 
  Loader2,
  X
} from 'lucide-react';
import { 
  db, 
  collection, 
  onSnapshot, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp 
} from '../firebase';
import { notify } from '../services/notify';

interface VariantAttribute {
  id: string;
  name: string; // e.g., 'Size', 'Color'
  orgId: string;
  values: string[]; // e.g., ['Small', 'Medium', 'Large']
  allowCustom: boolean;
  createdAt: any;
}

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());
const getLocalApiHeaders = () => ({
  Authorization: `Bearer ${getLocalAuthToken()}`,
  'Content-Type': 'application/json'
});

export default function VariantAttributesView({ orgId, language = 'en' }: { orgId: string, language?: string }) {
  const [attributes, setAttributes] = useState<VariantAttribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAttr, setEditingAttr] = useState<VariantAttribute | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    values: [] as string[],
    allowCustom: true,
  });

  const [currentValueInput, setCurrentValueInput] = useState('');

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      setLoading(true);
      void fetch(`/api/organizations/${orgId}/variant-attributes`, { headers: getLocalApiHeaders() })
        .then(async response => {
          const payload = await response.json().catch(() => null);
          if (!response.ok) throw new Error(payload?.error || 'Could not load product variants');
          return payload as VariantAttribute[];
        })
        .then(loadedAttributes => {
          if (!cancelled) setAttributes(loadedAttributes);
        })
        .catch(error => {
          console.error('Local API error loading product variants:', error);
          if (!cancelled) notify.error(error instanceof Error ? error.message : 'Failed to load product variants');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'variant_attributes'), where('orgId', '==', orgId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAttributes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VariantAttribute)));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching variant attributes:", error);
      setLoading(false);
      notify.error("Failed to load product variants.");
    });
    return () => unsubscribe();
  }, [orgId]);

  const handleAddValue = (e: React.KeyboardEvent | React.MouseEvent) => {
    if ('key' in e && e.key !== 'Enter') return;
    e.preventDefault();
    const val = currentValueInput.trim();
    if (val && !formData.values.includes(val)) {
      setFormData(prev => ({ ...prev, values: [...prev.values, val] }));
      setCurrentValueInput('');
    }
  };

  const removeValue = (val: string) => {
    setFormData(prev => ({ ...prev, values: prev.values.filter(v => v !== val) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    if (!formData.name) {
      notify.error('Attribute name is required');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        values: formData.values,
        allowCustom: formData.allowCustom,
      };

      if (isLocalSession()) {
        const endpoint = editingAttr
          ? `/api/organizations/${orgId}/variant-attributes/${editingAttr.id}`
          : `/api/organizations/${orgId}/variant-attributes`;
        const response = await fetch(endpoint, {
          method: editingAttr ? 'PATCH' : 'POST',
          headers: getLocalApiHeaders(),
          body: JSON.stringify(payload)
        });
        const saved = await response.json().catch(() => null) as VariantAttribute | null;
        if (!response.ok || !saved) throw new Error((saved as any)?.error || 'Could not save product variant');
        setAttributes(current => editingAttr
          ? current.map(attribute => attribute.id === saved.id ? saved : attribute)
          : [...current, saved].sort((left, right) => left.name.localeCompare(right.name))
        );
      } else {
        const data = {
          ...payload,
          orgId,
          updatedAt: serverTimestamp()
        };
        if (editingAttr) {
          await updateDoc(doc(db, 'variant_attributes', editingAttr.id), data);
        } else {
          await addDoc(collection(db, 'variant_attributes'), {
            ...data,
            createdAt: serverTimestamp()
          });
        }
      }

      notify.confirm(editingAttr ? "Attribute updated successfully" : "Attribute created successfully");
      setIsModalOpen(false);
      setEditingAttr(null);
      setFormData({ name: '', values: [], allowCustom: true });
    } catch (error) {
      console.error(error);
      notify.error(error instanceof Error ? error.message : "Failed to save attribute");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this variant attribute?")) return;
    try {
      if (isLocalSession()) {
        const response = await fetch(`/api/organizations/${orgId}/variant-attributes/${id}`, {
          method: 'DELETE',
          headers: getLocalApiHeaders()
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error || 'Could not delete product variant');
        }
        setAttributes(current => current.filter(attribute => attribute.id !== id));
      } else {
        await deleteDoc(doc(db, 'variant_attributes', id));
      }
      notify.confirm("Attribute deleted");
    } catch (error) {
      console.error(error);
      notify.error(error instanceof Error ? error.message : "Failed to delete attribute");
    }
  };

  const filteredAttrs = attributes.filter(a => 
    (a.name || '').toLowerCase().includes((searchQuery || '').toLowerCase())
  );

  return (
    <div className="p-6 space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold uppercase tracking-widest flex items-center gap-2">
            <Settings2 className="w-5 h-5 opacity-40" />
            Product Variants
          </h2>
          <p className="text-xs opacity-50 mt-1 font-mono">Manage global variant options like Size, Color, etc.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414]/10 px-4 py-2 rounded-xl w-64">
            <Search className="w-4 h-4 opacity-30" />
            <input 
              type="text" 
              placeholder="Search variants..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-xs w-full font-bold"
            />
          </div>
          <button 
            onClick={() => {
              setEditingAttr(null);
              setFormData({ name: '', values: [], allowCustom: true });
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-2.5 rounded-full hover:opacity-90 transition-all text-xs font-bold whitespace-nowrap shadow-xl"
          >
            <Plus className="w-4 h-4" />
            <span>Create Variant</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {loading ? (
          <div className="p-20 text-center"><Loader2 className="w-10 h-10 animate-spin mx-auto opacity-20" /></div>
        ) : filteredAttrs.length === 0 ? (
          <div className="p-20 text-center bg-white/30 border-2 border-dashed border-[#141414]/10 rounded-3xl opacity-40 italic serif uppercase tracking-widest">
            No variant attributes found. Create "Size", "Color", etc.
          </div>
        ) : (
          <div className="bg-white/50 border border-[#141414] rounded-3xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse font-mono text-sm">
                <thead>
                  <tr className="border-b border-[#141414] bg-[#141414] text-[#E4E3E0]">
                    <th className="p-4 text-xs font-bold uppercase tracking-widest">Variant Name</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest">Options</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest text-center">Custom Variant</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttrs.map(attr => (
                    <tr key={attr.id} className="border-b border-[#141414]/10 hover:bg-[#141414]/5 transition-colors group">
                      <td className="p-4 font-bold text-[#141414]">{attr.name}</td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1.5">
                          {(attr.values || []).map(v => (
                            <span key={v} className="bg-[#141414]/5 px-2 py-0.5 rounded text-[10px] font-bold tracking-tight">
                              {v}
                            </span>
                          ))}
                          {(!attr.values || attr.values.length === 0) && <span className="opacity-30 italic">No predefined options</span>}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        {attr.allowCustom ? (
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Allowed</span>
                        ) : (
                          <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Restricted</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => {
                              setEditingAttr(attr);
                              setFormData({
                                name: attr.name,
                                values: attr.values || [],
                                allowCustom: attr.allowCustom !== false
                              });
                              setIsModalOpen(true);
                            }}
                            className="p-2 hover:bg-black/5 rounded-xl transition-all"
                          >
                            <Edit2 className="w-4 h-4 opacity-50" />
                          </button>
                          <button 
                            onClick={() => handleDelete(attr.id)}
                            className="p-2 hover:bg-red-50 text-red-500 rounded-xl transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-white w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl border border-[#141414] shadow-2xl animate-in zoom-in duration-300">
            <div className="p-6 border-b border-[#141414]/10 flex items-center justify-between bg-[#141414] text-white">
              <h3 className="text-sm font-bold uppercase tracking-[0.2em]">{editingAttr ? 'Edit Variant' : 'Create Variant Group'}</h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-1 hover:bg-white/10 rounded-full transition-colors"
              >
                <Plus className="w-5 h-5 rotate-45" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase opacity-40 px-1">Variant Name</label>
                <input 
                  required
                  type="text" 
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. Size, Color, Material"
                  className="w-full bg-[#141414]/5 border-none rounded-2xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-[#141414]/20 outline-none transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase opacity-40 px-1">Default Options (Press Enter to add)</label>
                <div className="bg-[#141414]/5 rounded-2xl p-2 flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2 px-2 pt-2">
                    {formData.values.map(v => (
                      <div key={v} className="flex items-center gap-1 bg-[#141414] text-white px-2.5 py-1 rounded-full text-[10px] font-bold">
                        <span>{v}</span>
                        <button type="button" onClick={() => removeValue(v)} className="hover:text-red-300 ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <input 
                    type="text" 
                    value={currentValueInput}
                    onChange={e => setCurrentValueInput(e.target.value)}
                    onKeyDown={handleAddValue}
                    placeholder="Type an option and press Enter..."
                    className="w-full bg-transparent border-none px-3 py-2 text-sm font-bold outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
                <input 
                  type="checkbox" 
                  id="allowCustomModal"
                  checked={formData.allowCustom}
                  onChange={(e) => setFormData(prev => ({ ...prev, allowCustom: e.target.checked }))}
                  className="w-5 h-5 rounded border-blue-200 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="allowCustomModal" className="text-sm font-bold cursor-pointer select-none text-blue-900">
                  Allow Custom Variants
                  <span className="block text-[10px] opacity-60 font-normal mt-0.5">
                    Permit staff to enter custom values for this attribute outside of the predefined list above.
                  </span>
                </label>
              </div>

              <div className="pt-6">
                <button 
                  type="submit"
                  className="w-full bg-[#141414] text-white py-4 rounded-2xl font-bold uppercase tracking-[0.2em] shadow-xl hover:opacity-90 active:scale-[0.98] transition-all"
                >
                  {editingAttr ? 'Update Variant' : 'Save Variant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
